/**
 * One conversation: what the studio manager made of it (triage), the messages, and the reply box
 * (prefilled with the drafted reply when there is one). Nothing here sends without the artist
 * pressing Send.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import {
  Archive,
  ArrowLeft,
  ArrowRight,
  Forward,
  Inbox as InboxIcon,
  Loader2,
  Mail,
  Palette,
  RefreshCw,
  Send,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Trash2,
} from 'lucide-react'
import {
  useConvertToCommission,
  useMarkRead,
  useMoveThread,
  useReply,
  useRetriage,
  useSaveDraft,
  useThread,
} from '../hooks/useInbox'
import { AttachmentPicker } from './ComposeDialog'
import { MessageBody } from './MessageBody'
import { CategoryBadge } from './ThreadList'
import { isLead } from '../lib'
import type { Folder, ThreadMessage, Triage, TriageBrief } from '../types'

const addr = (raw: string | null) => {
  const m = String(raw || '').match(/<([^>]+)>/)
  return (m ? m[1] : String(raw || '')).trim().toLowerCase()
}

/** Everyone else on the newest inbound message — who "Reply all" would add. */
function otherParticipants(messages: ThreadMessage[], own: string, primary: string | null) {
  const last = messages.slice().reverse().find((m) => m.direction === 'inbound')
  if (!last) return []
  const skip = new Set([own.toLowerCase(), (primary || '').toLowerCase()])
  const out = new Set<string>()
  ;[...(last.to || []), ...(last.cc || []), last.from].forEach((a) => {
    const e = addr(a)
    if (e && !skip.has(e) && !/no-?reply@/.test(e)) out.add(e)
  })
  return Array.from(out)
}

const BRIEF_FIELDS: [keyof TriageBrief, string][] = [
  ['subject', 'Subject'],
  ['medium', 'Medium'],
  ['size', 'Size'],
  ['quantity', 'Quantity'],
  ['budget', 'Budget'],
  ['deadline', 'Deadline'],
  ['occasion', 'Occasion'],
  ['location', 'Location'],
]

function TriagePanel({ triage, onRetriage, retriaging }: { triage: Triage; onRetriage: () => void; retriaging: boolean }) {
  const brief = triage.brief || ({} as TriageBrief)
  const known = BRIEF_FIELDS.filter(([k]) => brief[k])
  return (
    <Card className="border-amber-200 bg-amber-50/40 dark:border-amber-900 dark:bg-amber-950/20">
      <CardContent className="space-y-3 pt-4 text-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 font-medium">
            <Sparkles className="h-4 w-4 text-amber-600" /> Studio manager
          </div>
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={onRetriage} disabled={retriaging}>
            <RefreshCw className={cn('h-3.5 w-3.5', retriaging && 'animate-spin')} />
          </Button>
        </div>
        {triage.summary && <p>{triage.summary}</p>}
        {known.length > 0 && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            {known.map(([k, label]) => (
              <div key={k} className="contents">
                <dt className="text-muted-foreground">{label}</dt>
                <dd>{brief[k] as string}</dd>
              </div>
            ))}
          </dl>
        )}
        {triage.missingInfo?.length > 0 && (
          <div>
            <div className="text-muted-foreground">Still needed before you can quote</div>
            <ul className="ml-4 list-disc">
              {triage.missingInfo.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}
        {triage.suggestedNextStep && (
          <p className="flex items-start gap-1.5">
            <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            {triage.suggestedNextStep}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

interface Props {
  threadId: number
  inboxAddress: string
  hasSignature: boolean
  commissionsHref: (id: number) => string
  onForward: (threadId: number, subject: string | null) => void
  onBack: () => void
}

export function ThreadView({ threadId, inboxAddress, hasSignature, commissionsHref, onForward, onBack }: Props) {
  const navigate = useNavigate()
  const { data: thread, isLoading } = useThread(threadId)
  const reply = useReply()
  const saveDraft = useSaveDraft()
  const retriage = useRetriage()
  const move = useMoveThread()
  const markRead = useMarkRead()
  const convert = useConvertToCommission()
  const [text, setText] = useState('')
  const [replyAll, setReplyAll] = useState(false)
  const [showCopies, setShowCopies] = useState(false)
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [files, setFiles] = useState<File[]>([])

  // Load the drafted reply into the composer whenever a different thread (or a fresh draft) arrives.
  useEffect(() => {
    setText(thread?.draftText || '')
  }, [thread?.id, thread?.draftText])

  // A different conversation starts with a plain reply.
  useEffect(() => {
    setReplyAll(false)
    setShowCopies(false)
    setCc('')
    setBcc('')
    setFiles([])
  }, [threadId])

  const others = useMemo(
    () => (thread ? otherParticipants(thread.messages, inboxAddress, thread.contactEmail) : []),
    [thread, inboxAddress]
  )

  if (isLoading || !thread) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  const triage = thread.triage as Triage
  const hasTriage = thread.triageStatus === 'done' && triage && triage.category
  const lead = hasTriage && isLead({ category: triage.category, commissionScore: triage.commissionScore })
  const dirty = text.trim() !== (thread.draftText || '').trim()
  const folder = thread.folder as Folder | 'sent'
  const moveTo = (f: Folder) => {
    move.mutate({ id: thread.id, folder: f })
    if (f !== 'inbox') onBack()
  }
  const send = () =>
    reply.mutate(
      { id: thread.id, mail: { text, cc, bcc, replyAll, files } },
      {
        onSuccess: () => {
          setText('')
          setFiles([])
          setCc('')
          setBcc('')
        },
      }
    )

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-2 border-b p-4 md:p-6">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-1">
            <Button variant="ghost" size="sm" className="-ml-2 shrink-0 md:hidden" onClick={onBack} aria-label="Back to list">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-lg font-semibold md:text-xl">{thread.subject || '(no subject)'}</h2>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-0.5">
            <Button variant="ghost" size="sm" onClick={() => onForward(thread.id, thread.subject)} title="Forward">
              <Forward className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                markRead.mutate({ id: thread.id, read: false })
                onBack()
              }}
              title="Mark as unread"
            >
              <Mail className="h-4 w-4" />
            </Button>
            {folder === 'inbox' || folder === 'sent' ? (
              <Button variant="ghost" size="sm" onClick={() => moveTo('archive')} title="Archive">
                <Archive className="h-4 w-4" />
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => moveTo('inbox')} title={folder === 'spam' ? 'Not spam' : 'Move to Inbox'}>
                {folder === 'spam' ? <ShieldCheck className="h-4 w-4" /> : <InboxIcon className="h-4 w-4" />}
              </Button>
            )}
            {folder !== 'spam' && folder !== 'trash' && (
              <Button variant="ghost" size="sm" onClick={() => moveTo('spam')} title="Mark as spam">
                <ShieldAlert className="h-4 w-4" />
              </Button>
            )}
            {folder !== 'trash' && (
              <Button variant="ghost" size="sm" onClick={() => moveTo('trash')} title="Move to Trash">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>
            {thread.contactName ? `${thread.contactName} · ` : ''}
            {thread.contactEmail}
          </span>
          <CategoryBadge category={thread.category} score={thread.commissionScore === null ? null : Number(thread.commissionScore)} />
          {thread.inquiryId && <span className="text-xs">via your website</span>}
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4 md:p-6">
        {hasTriage ? (
          <TriagePanel triage={triage} onRetriage={() => retriage.mutate(thread.id)} retriaging={retriage.isPending} />
        ) : thread.triageStatus === 'failed' ? (
          <Button variant="outline" size="sm" onClick={() => retriage.mutate(thread.id)} disabled={retriage.isPending}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" /> Triage this conversation
          </Button>
        ) : null}

        {(lead || thread.commissionId) && (
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-2 text-sm">
              <Palette className="h-4 w-4 text-primary" />
              {thread.commissionId ? 'This conversation is a commission project.' : 'This looks like a commission. Track it as one?'}
            </div>
            {thread.commissionId ? (
              <Button size="sm" variant="outline" onClick={() => navigate(commissionsHref(thread.commissionId as number))}>
                Open commission
              </Button>
            ) : (
              <Button size="sm" onClick={() => convert.mutate({ id: thread.id })} disabled={convert.isPending}>
                {convert.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                Make it a commission
              </Button>
            )}
          </div>
        )}

        <div className="space-y-3">
          {thread.messages.map((m) => (
            <div
              key={m.id}
              className={cn(
                'rounded-lg border p-3 text-sm md:p-4',
                m.direction === 'outbound' ? 'bg-primary/5 md:ml-8' : 'bg-background md:mr-8'
              )}
            >
              <div className="mb-2 space-y-0.5 text-xs text-muted-foreground">
                <div className="flex justify-between gap-2">
                  <span className="truncate font-medium text-foreground">{m.direction === 'outbound' ? 'You' : m.from}</span>
                  <span className="shrink-0">{m.receivedAt ? new Date(m.receivedAt).toLocaleString() : ''}</span>
                </div>
                {m.to?.length > 0 && <div className="truncate">To: {m.to.join(', ')}</div>}
                {m.cc?.length > 0 && <div className="truncate">Cc: {m.cc.join(', ')}</div>}
              </div>
              <MessageBody threadId={thread.id} message={m} />
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2 border-t p-3 md:p-4">
        {thread.draftText && !dirty && (
          <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-500">
            <Sparkles className="h-3 w-3" /> Drafted for you — read it, make it yours, then send.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-1 text-xs">
          <Button variant={replyAll ? 'ghost' : 'secondary'} size="sm" className="h-7" onClick={() => setReplyAll(false)}>
            Reply
          </Button>
          {others.length > 0 && (
            <Button
              variant={replyAll ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7"
              onClick={() => setReplyAll(true)}
              title={`Also to ${others.join(', ')}`}
            >
              Reply all
            </Button>
          )}
          {!showCopies && (
            <Button variant="ghost" size="sm" className="h-7" onClick={() => setShowCopies(true)}>
              Cc/Bcc
            </Button>
          )}
          <span className="ml-auto truncate text-muted-foreground">
            To {thread.contactName || thread.contactEmail || 'them'}
            {replyAll && others.length ? `, ${others.join(', ')}` : ''}
          </span>
        </div>
        {showCopies && (
          <div className="grid gap-2 sm:grid-cols-2">
            <Input placeholder="Cc" value={cc} onChange={(e) => setCc(e.target.value)} />
            <Input placeholder="Bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} />
          </div>
        )}
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder={`Reply to ${thread.contactName || thread.contactEmail || 'them'}…`}
        />
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="mr-auto">
            <AttachmentPicker files={files} onChange={setFiles} />
          </div>
          {hasSignature && <span className="hidden text-xs text-muted-foreground sm:inline">Signature added on send</span>}
          {dirty && (
            <Button variant="outline" size="sm" onClick={() => saveDraft.mutate({ id: thread.id, text })} disabled={saveDraft.isPending}>
              Save draft
            </Button>
          )}
          <Button
            size="sm"
            onClick={send}
            disabled={!text.trim() || reply.isPending}
          >
            {reply.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-2 h-3.5 w-3.5" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  )
}
